import { useContext } from 'react'
import { Box } from '@mui/material'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import { startCase } from 'lodash'
import AccessTimeIcon from '@mui/icons-material/AccessTime'

import { Property } from 'genesis-suite/types/networkTypes'
import useResourceMeta from '~/hooks/useResourceMeta'
import { PerspectiveContext } from './contexts/PerspectiveContext'
import { getRageOffSetForTimePeriod } from '~/lib/utils'
import TemporalDateSelect, { temporalDateSelectOptions } from './TemporalDateSelect'
import { makeStyles } from '@mui/styles'
interface SelectorProps {
    config: any
    onUpdate: (updatedConfig: any) => void
    categoryDateFormat?: string
    originalCategoryPropertyName?: string
    showTemporalSelector: boolean
}

const useStyles = makeStyles(theme => ({
    icon: {
        fill: theme.palette.text.primary,
    },
}))

export default function TemporalSelector({
    config,
    onUpdate,
    categoryDateFormat,
    originalCategoryPropertyName,
    showTemporalSelector,
}: SelectorProps) {
    const { filterByWidgetId, setFilterByWidgetId } = useContext(PerspectiveContext)
    const { ElementType, ElementName } = config?.ChartConfig?.Source || {}
    const categoryPropertyName = config?.ChartConfig?.LabelField

    const options = useOptions(ElementType, ElementName, categoryPropertyName)
    const categoryOption = options?.find(o => o.displayName === 'Default')
    const classes = useStyles()
    let emptyFilter: any = {
        IsTemporal: true,
        Values: [],
        DisplayValues: [],
        RangeOffset: {},
        UseLastRefreshDate: false,
        Operator: 'EqualTo',
        PostCalculation: false,
    }

    if (categoryOption?.container) {
        emptyFilter = {
            ...emptyFilter,
            FilterName: `search|${categoryOption.name}`,
            ResourceName: categoryOption.container.name,
            ResourceType: categoryOption.container.type,
            PropertyName: categoryOption.name,
        }
    }

    function handleUpdate(labelField) {
        onUpdate({
            ...config,
            ChartConfig: {
                ...config.ChartConfig,
                LabelField: labelField,
                CategoryDateFormat: labelField === originalCategoryPropertyName ? categoryDateFormat : null,
            },
        })
    }

    function handlefilterUpdate(id) {
        if (id === '0') {
            setFilterByWidgetId(s => ({ ...s, [config.Id]: { ...filterByWidgetId?.[config.Id], temporal: [] } }))
        } else {
            const selectedOption = temporalDateSelectOptions.find(obj => obj.id === id)
            emptyFilter['RangeOffset'] = getRageOffSetForTimePeriod(selectedOption?.id)
            setFilterByWidgetId(s => ({
                ...s,
                [config.Id]: { ...filterByWidgetId?.[config.Id], temporal: [emptyFilter] },
            }))
        }
    }

    return (
        <Box
            sx={{
                minWidth: '100px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 1,
                minHeight: '25px',
            }}
        >
            {options.length > 1 && (
                <>
                    {showTemporalSelector && (
                        <Box
                            sx={{
                                minWidth: '100px',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                gap: 1,
                            }}
                        >
                            <AccessTimeIcon />
                            <Select
                                onChange={e => handleUpdate(e.target.value as string)}
                                sx={theme => ({
                                    '& .MuiSelect-select': {
                                        pl: 1,
                                        py: 0.2,
                                    },
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: theme.palette.text.primary,
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: theme.palette.text.primary,
                                    },
                                })}
                                value={categoryPropertyName}
                                variant="outlined"
                                inputProps={{
                                    classes: {
                                        icon: classes.icon,
                                    },
                                }}
                            >
                                {options.map(option => (
                                    <MenuItem key={option.name} value={option.name}>
                                        {option.displayName}
                                    </MenuItem>
                                ))}
                            </Select>
                        </Box>
                    )}

                    <Box sx={{ position: 'absolute', right: '10px' }}>
                        <TemporalDateSelect handlefilterUpdate={handlefilterUpdate} />
                    </Box>
                </>
            )}
        </Box>
    )
}

interface Option extends Property {
    displayName: string
}

function useOptions(resourceType, resourceName, categoryPropertyName): Option[] {
    const [metaDataSource] = useResourceMeta(resourceType, resourceName)

    if (!metaDataSource?.properties) return []

    let categoryInsightProperty = metaDataSource.properties.find(n => n.name == categoryPropertyName)

    if (categoryInsightProperty?.referenceAttributeId) {
        categoryInsightProperty = metaDataSource?.properties?.find(
            n => n.id == categoryInsightProperty.referenceAttributeId
        )
    }

    return metaDataSource.properties
        ?.filter(
            p =>
                (p != null &&
                    p.computed &&
                    categoryInsightProperty?.id &&
                    p.referenceAttributeId == categoryInsightProperty?.id &&
                    !p.name.toLowerCase().endsWith('halfyearname')) ||
                p.id === categoryInsightProperty?.id
        )
        ?.map(p => ({
            ...p,
            displaySortOrder: getTemporalPropertyOrder(p.name, categoryInsightProperty?.name),
            displayName: startCase(getTemporalPropertyName(p.name, categoryInsightProperty?.name)),
        }))
        ?.sort((a, b) => {
            if (a.displaySortOrder > b.displaySortOrder) return 1
            if (a.displaySortOrder < b.displaySortOrder) return -1
            return 0
        })
}

function getTemporalPropertyName(temporalPropertyName, referencePropertyName) {
    if (!temporalPropertyName || temporalPropertyName.length < 1) return 'Default'
    if (!referencePropertyName || referencePropertyName.length < 1) return temporalPropertyName
    if (temporalPropertyName === referencePropertyName) return 'Default'
    return temporalPropertyName.substring(referencePropertyName.length)
}
function getTemporalPropertyOrder(temporalPropertyName, referencePropertyName) {
    if (!temporalPropertyName || temporalPropertyName.length < 1) return 0
    if (!referencePropertyName || referencePropertyName.length < 1) return 0
    temporalPropertyName = temporalPropertyName.substring(referencePropertyName.length)

    switch (temporalPropertyName.toLowerCase()) {
        case 'year':
            return 1
        case 'halfyear':
            return 2
        case 'quarter':
            return 3
        case 'quartername':
            return 4
        case 'monthyear':
            return 5
        case 'month':
            return 6
        case 'monthname':
            return 7
        case 'fullmonthname':
            return 8
        case 'week':
            return 9
        case 'isoweek':
            return 10
        case 'day':
            return 11
        case 'hour':
            return 12
        case 'minute':
            return 13
        case 'second':
            return 14
        default:
            return 0
    }
}
